import type { FC, PropsWithChildren } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { translate } from "app/filters/i18nFilter";

import type { IRankSignals } from "../websiteRankings.types";

import { RankChangeValue } from "./RankChangeValue";

interface IChangeWrapperProps extends PropsWithChildren {
    "data-automation": string;
}

const ChangeWrapper = ({ children, ...rest }: IChangeWrapperProps) => (
    <Flex
        justifyContent="flex-end"
        alignItems="center"
        columnGap="2px"
        padding="0 12px 0 0"
        lineHeight="1"
        {...rest}
    >
        {children}
    </Flex>
);

type RankChangeRow = Partial<IRankSignals>;

export const RankChangeCell: FC<Pick<ITableCellProps<number | null, RankChangeRow>, "row">> = ({ row }) => {
    if (row?.IsNewEntrant) {
        return (
            <ChangeWrapper data-automation="website-rankings-rank-change-new">
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.green["400_P"]}
                    data-automation="website-rankings-rank-change-new-label"
                >
                    {translate("website.rankings.new.entrant.badge").toUpperCase()}
                </Text>
            </ChangeWrapper>
        );
    }

    // Every numeric state — a delta, a held position, and a missing one — goes through
    // ChangeCell via RankChangeValue, so arrows, colours and the no-change sign stay
    // consistent with the other change columns rather than being restyled here.
    return (
        <ChangeWrapper data-automation="website-rankings-rank-change">
            <RankChangeValue value={row?.RankChange} />
        </ChangeWrapper>
    );
};

RankChangeCell.displayName = "RankChangeCell";
